import { SignalStoreBuilder } from "src/app/_stores/SignalStoreBuilder";
import { TwinChargerDetailSnapshot } from "src/app/_models/location/TwinCharger";
import { TwinChargerDetailSnapshotDto } from "src/app/_models/location/LocationDTO";

type TwinChargerSnapshotMap = {
  id_location: number;
  snapshots: TwinChargerDetailSnapshot[];
  isRefreshing: boolean;
};

export const SignalTwinChargerDetailSnapshotStore =
  new SignalStoreBuilder<TwinChargerSnapshotMap>(
    (entry) => entry.id_location,
    (json) => {
      const id = json["id_location"] as number;
      const snapshots = (json["snapshots"] as unknown[]).map((s) =>
        TwinChargerDetailSnapshot.fromDto(
          TwinChargerDetailSnapshotDto.fromJson(s),
        ),
      );
      return { id_location: id, snapshots, isRefreshing: false };
    },
  )
    .withMethod("loadAllLatest", (_store, http, { upsert }) => async () => {
      const response = await http.get("location/twinChargers/data", {
        hideLoader: true,
      });
      if (!response) return;
      for (const item of response as unknown as {
        id_location: number;
        snapshot: unknown;
      }[]) {
        if (!item.snapshot) continue;
        const incoming = TwinChargerDetailSnapshot.fromDto(
          TwinChargerDetailSnapshotDto.fromJson(item.snapshot),
        );
        const existing = _store.entityMap()[item.id_location]?.snapshots ?? [];
        upsert({
          id_location: item.id_location,
          snapshots: [
            incoming,
            ...existing.filter(
              (s) =>
                s.id_twincharger_snapshot !== incoming.id_twincharger_snapshot,
            ),
          ],
          isRefreshing: false,
        });
      }
    })
    .withMethod(
      "loadLatest",
      (_store, http, { upsert }) =>
        async (id_location: number) => {
          upsert({
            id_location,
            snapshots: _store.entityMap()[id_location]?.snapshots ?? [],
            isRefreshing: true,
          });
          const response = await http.get(
            `location/twinChargers/${id_location}/snapshots?limit=1`,
            { hideLoader: true },
          );
          if (!response) {
            upsert({
              id_location,
              snapshots: _store.entityMap()[id_location]?.snapshots ?? [],
              isRefreshing: false,
            });
            return;
          }
          const incoming = (
            ((response as any as Record<string, unknown>)[
              "snapshots"
            ] as unknown[]) ?? []
          ).map((s) =>
            TwinChargerDetailSnapshot.fromDto(
              TwinChargerDetailSnapshotDto.fromJson(s),
            ),
          );
          const existing = _store.entityMap()[id_location]?.snapshots ?? [];
          upsert({
            id_location,
            snapshots: [
              ...incoming,
              ...existing.filter(
                (s) =>
                  !incoming.some(
                    (i) =>
                      i.id_twincharger_snapshot === s.id_twincharger_snapshot,
                  ),
              ),
            ],
            isRefreshing: false,
          });
        },
    )
    .withMethod(
      "loadSnapshots",
      (_store, http, { upsert }) =>
        async (id_location: number, limit: number = 120) => {
          upsert({
            id_location,
            snapshots: _store.entityMap()[id_location]?.snapshots ?? [],
            isRefreshing: true,
          });
          const response = await http.get(
            `location/twinChargers/${id_location}/snapshots?limit=${limit}`,
            { hideLoader: true },
          );
          if (!response) {
            upsert({
              id_location,
              snapshots: _store.entityMap()[id_location]?.snapshots ?? [],
              isRefreshing: false,
            });
            return;
          }
          const snapshots = (
            (response as any as Record<string, unknown>)[
              "snapshots"
            ] as unknown[]
          ).map((s) =>
            TwinChargerDetailSnapshot.fromDto(
              TwinChargerDetailSnapshotDto.fromJson(s),
            ),
          );
          upsert({ id_location, snapshots, isRefreshing: false });
        },
    )
    .withAutoRefresh((store) => store.loadAllLatest())
    .providedInRoot()
    .build();
